export const MILESTONE_TYPE = {
  prediction: 'Milestone Prediction',
  observation: 'Milestone Observation',
} as const

export type MilestoneTypeName =
  (typeof MILESTONE_TYPE)[keyof typeof MILESTONE_TYPE]

export type TimelineLineSegment = 'only' | 'first' | 'middle' | 'last'

/** Figma layout tokens for shipment/container milestone cards. */
export const MILESTONE_TIMELINE = {
  gutterWidth: '28px',
  dotSize: '12px',
  dotCenter: '14px',
  gutterRowHeight: '28px',
  lineWidth: '2px',
  rowGap: '8px',
  rowPaddingTop: '4px',
  rowPaddingBottom: '28px',
  pillGap: '8px',
  pillColumnGap: '4px',
  pillIconSize: '16px',
  pillPadding: '4px 10px',
  pillInnerGap: '6px',
  titleRowHeight: '20px',
  titlePaddingX: '10px',
} as const

export const MILESTONE_SKELETON = {
  lineSegments: ['first', 'middle', 'middle', 'last'] as TimelineLineSegment[],
  titleWidth: '180px',
  pillWidth: '100px',
  titleHeight: '14px',
} as const

/** Flex items need min-width 0 to shrink inside narrow bento columns. */
export const MILESTONE_FLEX_SHRINK = {
  minW: 0,
} as const

export const MILESTONE_TEXT_WRAP = {
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
} as const

export const MILESTONE_TYPOGRAPHY = {
  pill: {
    fontSize: '12px',
    fontWeight: '500',
  },
  title: {
    fontSize: '14px',
    fontWeight: {
      first: '700',
      default: '500',
    },
  },
} as const

export const MilestoneLayout = {
  flexItem: () => MILESTONE_FLEX_SHRINK,
  flexRow: () => ({
    ...MILESTONE_FLEX_SHRINK,
    alignSelf: 'stretch' as const,
  }),
  rowContainer: () => ({
    display: 'flex' as const,
    flexDirection: 'row' as const,
    alignItems: 'stretch' as const,
    ...MILESTONE_FLEX_SHRINK,
    alignSelf: 'stretch' as const,
  }),
  columnList: () => ({
    alignItems: 'stretch' as const,
    flexDirection: 'column' as const,
    ...MILESTONE_FLEX_SHRINK,
  }),
  rowContent: () => ({
    flex: '1 1 0',
    flexDirection: 'column' as const,
    gap: MILESTONE_TIMELINE.rowGap,
    pb: MILESTONE_TIMELINE.rowPaddingBottom,
    pt: MILESTONE_TIMELINE.rowPaddingTop,
    ...MILESTONE_FLEX_SHRINK,
  }),
  titleRow: () => ({
    h: MILESTONE_TIMELINE.titleRowHeight,
    alignItems: 'center' as const,
    px: MILESTONE_TIMELINE.titlePaddingX,
    ...MILESTONE_FLEX_SHRINK,
  }),
  pillColumns: () => ({
    gap: MILESTONE_TIMELINE.pillGap,
    alignItems: 'start' as const,
    ...MILESTONE_FLEX_SHRINK,
    alignSelf: 'stretch' as const,
  }),
  pillColumn: () => ({
    flex: '1 1 0',
    flexDirection: 'column' as const,
    gap: MILESTONE_TIMELINE.pillColumnGap,
    ...MILESTONE_FLEX_SHRINK,
  }),
  root: () => ({
    display: 'flex' as const,
    flexDirection: 'column' as const,
    alignSelf: 'stretch' as const,
    flex: '1' as const,
    ...MILESTONE_FLEX_SHRINK,
  }),
  pillTextColor: (type: MilestoneTypeName) =>
    type === MILESTONE_TYPE.prediction ? 'neutral.70' : 'turquoise.100',
  titleTextColor: (type: MilestoneTypeName) =>
    `presets.${type === MILESTONE_TYPE.prediction ? 'neutral.70' : 'turquoise.100'}`,
  titleFontWeight: (index: number) =>
    index === 0
      ? MILESTONE_TYPOGRAPHY.title.fontWeight.first
      : MILESTONE_TYPOGRAPHY.title.fontWeight.default,
} as const

export type MilestoneVariant = 'detailDrawer' | 'cluster'

export type MilestoneDisplayOptions = {
  hideShipmentDocumentId: boolean
  hideIssuer: boolean
}

export const MILESTONE_VARIANTS: Record<
  MilestoneVariant,
  MilestoneDisplayOptions
> = {
  detailDrawer: {
    hideShipmentDocumentId: false,
    hideIssuer: false,
  },
  cluster: {
    hideShipmentDocumentId: true,
    hideIssuer: true,
  },
}
